import React, { useEffect, useRef, useState } from 'react';
import { toast, ToastOptions, Zoom } from 'react-toastify';
import {
  getAssignedUrl,
  sendBucketImage,
} from '../../../../../services/api/bucket';
import { handleErrorMessage } from '../../../../../utils/handleErrorMessage';

import imagePlaceholder from '../../../../../assets/img/userPhoto.png';

import styles from './profile-image.module.css';
import { ImageModal } from '../../../../../shared/ImageInput/ImageModal';

interface Props {
  onUploadComplete?: (fileUrl: string | null) => void;
  imgUrl?: string | null;
}

export const ProfileImage = ({ onUploadComplete, imgUrl = null }: Props) => {
  const [imageUrl, setImageUrl] = useState<string | null>(imgUrl);
  const [isUploading, setIsUploading] = useState(false);

  const [showImageModal, setShowImageModal] = useState(false);

  const imageInput = useRef<HTMLInputElement>(null);

  const toastConfig: ToastOptions = {
    position: toast.POSITION.BOTTOM_LEFT,
    autoClose: false,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: false,
    draggable: true,
    progress: undefined,
    theme: 'dark',
    transition: Zoom,
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const toastId = toast.info(`Enviando ${file.name}...`, {
      ...toastConfig,
      autoClose: false,
    });

    try {
      // 1) obter URL assinada para upload
      const assignedUrlData = await getAssignedUrl(file.name, 'profile');

      toast.update(toastId, {
        render: `Enviando ${file.name}...`,
        type: 'info',
        autoClose: false,
      });

      // 2) enviar para o bucket
      await sendBucketImage(file, assignedUrlData.url);

      toast.update(toastId, {
        render: `Upload concluído!`,
        type: 'success',
        autoClose: 2500,
      });

      setImageUrl(assignedUrlData.image_url);
      onUploadComplete?.(assignedUrlData.image_url);
    } catch (err) {
      const message = handleErrorMessage(err);
      toast.update(toastId, {
        render: `Erro: ${message}`,
        type: 'error',
        autoClose: 5000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelectImage = () => {
    imageInput.current?.click();
  };
  const handleImageRemove = () => {
    setImageUrl(null);
    onUploadComplete?.(null);
  };

  useEffect(() => {
    setImageUrl(imgUrl);
  }, [imgUrl]);

  return (
    <div className={styles.container}>
      {showImageModal && (
        <ImageModal
          selectedImage={imageUrl}
          setSelectedImage={() => setShowImageModal(false)}
          onlyView
          toolbarStyle={{
            bottom: '50px',
          }}
        />
      )}

      <button
        onClick={() => setShowImageModal(true)}
        type="button"
        style={{ all: 'unset' }}
      >
        <div className={styles['image-preview']}>
          {imageUrl ? (
            <img src={imageUrl} alt="Imagem de perfil" />
          ) : (
            <img src={imagePlaceholder} alt="Imagem de perfil" />
          )}
        </div>
      </button>

      <div className={styles.actions}>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          disabled={isUploading}
          ref={imageInput}
          className={styles['image-input']}
        />
        <button type="button" onClick={handleSelectImage}>
          Adicionar Imagem
        </button>
        <button
          type="button"
          onClick={handleImageRemove}
          disabled={isUploading || !imageUrl}
        >
          Remover Imagem
        </button>
      </div>
    </div>
  );
};
